import { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Plus, UserCircle } from 'lucide-react';
import { cn } from '../../lib/cn.js';
import { BOTTOM_NAV_END, BOTTOM_NAV_START, CREATE_ACTIONS, isActivePath, type ShellNavItem } from './navItems.js';

const PROFILE: ShellNavItem = { to: '/profile', labelKey: 'shell.navPersonalArea', icon: UserCircle };

export function BottomNav({ desktop = false }: { desktop?: boolean }) {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [openPlus, setOpenPlus] = useState(false);
  const plusRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (plusRef.current && !plusRef.current.contains(e.target as Node)) setOpenPlus(false);
    };
    if (openPlus) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [openPlus]);

  const renderLink = (item: ShellNavItem, iconOnly = false) => {
    const isActive = isActivePath(location.pathname, item);
    const Icon = item.icon;
    return (
      <Link
        key={item.to}
        to={item.to}
        aria-label={t(item.labelKey)}
        className={cn(
          'flex flex-col items-center justify-center gap-0.5 px-2 py-1.5 rounded-lg transition-colors duration-200 min-w-0',
          isActive ? 'text-primary' : 'text-muted-foreground',
        )}
      >
        <Icon className={iconOnly ? 'h-6 w-6 shrink-0' : 'h-5 w-5 shrink-0'} />
        {/* Two lines, not a truncation. A slot is a quarter of a phone screen
            and the labels are the screens' full names — "היסטוריית שליחות"
            cut to one line read as "היסטוריית …", which names nothing. The
            bar is 64px tall and a wrapped label still clears it. */}
        {!iconOnly && (
          <span className="text-[10px] font-medium leading-tight text-center line-clamp-2 max-w-[72px]">{t(item.labelKey)}</span>
        )}
      </Link>
    );
  };

  return (
    /* Above the dialog layer (`z-[60]`), not under it: dialogs now stop short
       of the bar, and a bar drawn behind the scrim would be dimmed and dead
       while it is plainly visible. */
    <nav className={cn('fixed bottom-0 left-0 right-0 z-[70] bg-card border-t border-border pb-[env(safe-area-inset-bottom)]', !desktop && 'md:hidden')} aria-label={t('nav.menu')}>
      <div className="flex items-center justify-around h-16 px-1">
        {BOTTOM_NAV_START.map((item) => renderLink(item))}

        <div className="relative" ref={plusRef}>
          <button
            type="button"
            onClick={() => setOpenPlus((v) => !v)}
            aria-label={t('shell.newAction')}
            aria-expanded={openPlus}
            className="h-14 w-14 -mt-6 rounded-2xl bg-primary text-primary-foreground flex items-center justify-center shadow-lg active:scale-95 transition-transform"
          >
            <Plus className={cn('h-7 w-7 transition-transform', openPlus && 'rotate-45')} />
          </button>
          {/* Centred with auto margins, not `-translate-x-1/2`: this element
              also runs `animate-fade-up`, whose keyframes end at
              `transform: translateY(0)` and whose `both` fill mode keeps that
              applied — an animated declaration outranks a normal one, so the
              translate was dropped and the menu sat half its width too far
              over, hanging off the edge of the screen. */}
          {openPlus && (
            <div className="fixed bottom-24 left-0 right-0 mx-auto z-[100] w-60 bg-card border border-border rounded-xl shadow-xl overflow-hidden animate-fade-up">
              {CREATE_ACTIONS.map((action, index) => {
                const Icon = action.icon;
                return (
                  <button
                    key={action.to}
                    type="button"
                    onClick={() => {
                      setOpenPlus(false);
                      navigate(action.to);
                    }}
                    className={cn(
                      'w-full flex items-center gap-2.5 px-4 py-3 text-sm hover:bg-muted/60 transition-colors text-start',
                      index > 0 && 'border-t border-border',
                    )}
                  >
                    <Icon className={cn('h-4 w-4', action.tone === 'muted' ? 'text-muted-foreground' : 'text-primary')} />
                    {t(action.labelKey)}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {BOTTOM_NAV_END.map((item) => renderLink(item))}
        {renderLink(PROFILE, true)}
      </div>
    </nav>
  );
}
